/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from "react";
import { EvidencePart } from "@interfaces/Interfaces";
import "./EvidenceParts.css";
import { IonItem, IonRadio, IonRadioGroup } from "@ionic/react";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import DOMPurify from "dompurify";
import { Text, View } from "@react-pdf/renderer";
import { Html } from "react-pdf-html";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import DataController from "@controllers/DataController";
import * as EditorUtils from "@utils/editorUtils";
import classNames from "classnames";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any, isInput?: boolean) => void;
  onInputBlurred: () => void;
  focusRichText: () => void;
  blurRichText: () => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
  id?: any;
};

const RadioButtonWithComments: React.FC<Props> = ({
  definition,
  onValueChanged,
  focusRichText,
  blurRichText,
  data,
  isDisplay,
  isPDF,
  pdfStyles,
  initialValue,
  id,
}) => {
  const ref = useRef<TinyMCEEditor | null>(null);

  const [radioValue, setRadioValue] = useState<string>(initialValue?.radioValue || "");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [listValues, setListValues] = useState<string[]>(["Yes", "No"]);

  const [textValue, setTextValue] = useState<string>(initialValue?.textValue || "");

  const radioValueChanged = (event: any): void => {
    const _value = event.detail.value;

    setRadioValue(_value);
    onValueChanged && onValueChanged({ radioValue: _value, textValue });
  };

  const textValueChanged = (change: any): void => {
    setTextValue(change);
    onValueChanged && onValueChanged({ radioValue, textValue: change }, true);
  };

  const sanitizeData = (data: string) => ({ __html: DOMPurify.sanitize(data) });

  if (isPDF) {
    return (
      <>
        <View>
          <Text style={pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(definition)}</Text>
          <Text style={pdfStyles.evidenceDate}>{data.radioValue}</Text>
        </View>
        {data.textValue && data.textValue.length > 0 && (
          <>
            <View>
              <Text style={pdfStyles.reflectionHeader}>{"Comments:"}</Text>
              <Html
                style={{ fontSize: 12 }}
                stylesheet={htmlStyles}
              >
                {DataController.decodeHTML(data?.textValue)}
              </Html>
            </View>
          </>
        )}
      </>
    );
  }

  if (isDisplay) {
    return (
      <div className="readEvidenceBlock">
        <div className="readEvidenceHeader">{definition["Display Title"] || definition.Name}</div>
        <div className="readEvidenceText">{data.radioValue}</div>
        {data.textValue && (
          <>
            <div className="readEvidenceHeader mt-4">{"Comments"}</div>
            <div
              className="readEvidenceText"
              dangerouslySetInnerHTML={sanitizeData(data.textValue)}
            />
          </>
        )}
        <div className="mt-4 h-[1px] w-full bg-grey-30" />
      </div>
    );
  }

  return (
    <div>
      <IonRadioGroup
        color="light"
        id="editRoleRadioGroup"
        value={radioValue}
        onIonChange={radioValueChanged}
      >
        {listValues.map((item) => {
          return (
            <IonItem
              className="evidencePartsRadioContainer"
              key={item}
            >
              <IonRadio
                aria-label={item}
                mode="md"
                value={item}
                justify="space-between"
                className={classNames("text-17px tracking-[-0.3]", { "!font-semibold": radioValue === item })}
              >
                {item}
              </IonRadio>
            </IonItem>
          );
        })}
      </IonRadioGroup>
      <Editor
        tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
        id={id}
        onInit={(event, editor) => (ref.current = editor)}
        value={textValue}
        onEditorChange={(a, editor) => {
          a.length > 0 && editor.getBody().setAttribute("data-mce-placeholder", "");
          textValueChanged(a);
        }}
        onFocus={() => focusRichText()}
        onBlur={() => blurRichText()}
        init={{
          height: EditorUtils.getInitialHeight(),
          placeholder: definition?.["Placeholder Text"] || "Add your additional comments here...",
          browser_spellcheck: true,
          branding: false,
          menubar: false,
          paste_data_images: false,
          content_style: EditorUtils.editorContentStyle,
          content_css: "/EvidenceParts.css",
          plugins: ["autolink", "wordcount", "lists"],
          invalid_styles: {
            "*": "color line-height mso-outline-level background font-family",
          },
          style_formats: [
            {
              title: "Headings",
              items: [
                { title: "Heading 1", block: "h1" },
                { title: "Heading 2", block: "h2" },
                { title: "Heading 3", block: "h3" },
              ],
            },
            {
              title: "Inline",
              items: [
                { title: "Bold", block: "strong" },
                { title: "Italic", block: "em" },
                { title: "Underline", block: "u" },
                { title: "Strikethrough", block: "s" },
              ],
            },
            {
              title: "Blocks",
              items: [
                { title: "Paragraph", block: "p" },
                { title: "Blockquote", block: "blockquote" },
              ],
            },
          ],
          fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
          toolbar: [
            "undo redo | styles fontsize | bold italic underline | bullist numlist | alignleft aligncenter alignright",
          ],
          mobile: {
            toolbar_mode: "sliding",
          },
        }}
        licenseKey="gpl"
      />
    </div>
  );
};

const htmlStyles = {
  p: {
    fontFamily: "Inter",
    marginTop: 8,
    marginBottom: 8,
  },
  b: {
    fontFamily: "Inter",
    fontWeight: 700,
  },
  i: {
    fontFamily: "Inter",
    fontStyle: "italic",
  },
  em: {
    fontFamily: "Inter",
    fontStyle: "italic",
  },
  strong: {
    fontFamily: "Inter",
    fontWeight: 700,
  },
};

export default RadioButtonWithComments;
